import React from 'react';
import { Building, Plus, Snowflake, Zap, Wifi, AlertTriangle } from 'lucide-react';
import { Rack, RackType, Floor } from '../types';

// Define the props for our RackGrid component
interface RackGridProps {
  racks: Rack[];
  selectedRack: number | null;
  setSelectedRack: (rackId: number) => void;
  rackTypes: RackType[];
  floor?: Floor | null;
}

type RackStatus = 'empty' | 'ok' | 'alert' | 'warning' | 'critical';

function getRackStatus(rack: Rack, rackType: RackType | null | undefined): RackStatus {
  if (!rack.type || !rackType) return 'empty';
  const servers = rack.servers ?? [];
  if (servers.length === 0) return 'ok';
  const hasFailed = servers.some(s => s.failureStatus === 'failed');
  const hasRepairing = servers.some(s => s.failureStatus === 'repairing');
  const usedPower = rack.usedPower ?? servers.reduce((s, srv) => s + (srv.powerDraw ?? 0), 0);
  const powerPct = rackType.power > 0 ? usedPower / rackType.power : 0;
  if (hasFailed) return 'critical';
  if (hasRepairing) return 'alert';
  if (powerPct > 0.90) return 'warning';
  return 'ok';
}

function rackStatusClasses(status: RackStatus, baseColor: string): { border: string; glow: string; badge: React.ReactNode } {
  switch (status) {
    case 'critical':
      return { border: 'border-red-500 ring-1 ring-red-500/60', glow: 'shadow-red-700/50 shadow-md', badge: <AlertTriangle className="w-3 h-3 text-red-300 absolute top-0.5 right-0.5" /> };
    case 'alert':
      return { border: 'border-amber-400 ring-1 ring-amber-400/50', glow: 'shadow-amber-600/40 shadow-sm', badge: <AlertTriangle className="w-3 h-3 text-amber-300 absolute top-0.5 right-0.5" /> };
    case 'warning':
      return { border: 'border-orange-400 ring-1 ring-orange-400/40', glow: '', badge: <Zap className="w-3 h-3 text-orange-300 absolute top-0.5 right-0.5" /> };
    default:
      return { border: baseColor, glow: '', badge: null };
  }
}

const RackGrid: React.FC<RackGridProps> = ({
  racks,
  selectedRack,
  setSelectedRack,
  rackTypes,
  floor,
}) => {
  // If a design grid exists, render dynamically from it
  const grid = floor?.designGrid;
  if (grid && grid.length) {
    const rackByPos = new Map<string, Rack>();
    racks.forEach((r) => {
      if (typeof r.x === 'number' && typeof r.z === 'number') {
        rackByPos.set(`${r.z}-${r.x}`, r);
      }
    });

    // Shared cell base classes — aspect-square + w-full fills the 1fr column,
    // min-w-0 prevents overflow, rounded + border + flex center are always needed.
    const cellBase = 'aspect-square w-full min-w-0 box-border rounded flex flex-col items-center justify-center border-2 overflow-hidden';

    return (
      <div className="bg-gray-800 rounded-lg p-3">
        {grid.map((row, rowIndex) => {
          const allEmpty = row.every((cell) => cell.type === 'empty');
          if (allEmpty) return null;

          const hasRack = row.some((cell) => cell.type === 'rack');
          const allowWithHot = (t: string) =>
            t === 'hot-aisle' || t === 'empty' || t === 'cooling' || t === 'power' || t === 'networking';
          const allowWithCold = (t: string) =>
            t === 'cold-aisle' || t === 'empty' || t === 'cooling' || t === 'power' || t === 'networking';
          const hasHotTile = row.some((cell) => cell.type === 'hot-aisle');
          const hasColdTile = row.some((cell) => cell.type === 'cold-aisle');
          const isHot = !hasRack && hasHotTile && row.every((cell) => allowWithHot(cell.type));
          const isCold = !hasRack && hasColdTile && row.every((cell) => allowWithCold(cell.type));

          if (isHot) {
            return (
              <div
                key={`hot-${rowIndex}`}
                className="text-center py-2 mb-1 bg-gradient-to-r from-red-900 to-rose-700 rounded text-red-200 text-sm font-semibold"
              >
                🔥 Hot Aisle - Server Exhaust
              </div>
            );
          }
          if (isCold) {
            return (
              <div
                key={`cold-${rowIndex}`}
                className="text-center py-2 mb-1 bg-gradient-to-r from-cyan-900 to-blue-700 rounded text-blue-200 text-sm font-semibold"
              >
                ❄️ Cold Aisle - Cool Air Intake
              </div>
            );
          }

          return (
            <div
              key={`row-${rowIndex}`}
              className="grid gap-1 mb-1 p-1 bg-gray-700/30 rounded border border-gray-600/50"
              style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}
            >
              {row.map((cell, colIndex) => {
                const cellType = (cell as any).type as string;

                if (cellType !== 'rack') {
                  let extra = '';
                  let title = '';
                  let content: React.ReactNode = null;

                  switch (cellType) {
                    case 'cooling':
                      extra = 'bg-teal-700/40 border-teal-500/50 text-teal-200';
                      title = 'Cooling Unit';
                      content = <Snowflake className="w-4 h-4" />;
                      break;
                    case 'power':
                      extra = 'bg-yellow-700/30 border-yellow-500/50 text-yellow-200';
                      title = 'Power Distribution';
                      content = <Zap className="w-4 h-4" />;
                      break;
                    case 'networking':
                      extra = 'bg-green-700/30 border-green-500/50 text-green-200';
                      title = 'Network / MDF';
                      content = <Wifi className="w-4 h-4" />;
                      break;
                    case 'hot-aisle':
                      extra = 'bg-red-900/30 border-red-600/40 text-red-200';
                      title = 'Hot Aisle';
                      content = <span className="text-xs">🔥</span>;
                      break;
                    case 'cold-aisle':
                      extra = 'bg-blue-900/30 border-blue-600/40 text-blue-200';
                      title = 'Cold Aisle';
                      content = <span className="text-xs">❄️</span>;
                      break;
                    default:
                      extra = 'border-gray-600/20 bg-gray-700/10';
                      title = '';
                  }

                  return (
                    <div key={`cell-${rowIndex}-${colIndex}`} className={`${cellBase} ${extra}`} title={title}>
                      {content}
                    </div>
                  );
                }

                const rack = rackByPos.get(`${rowIndex}-${colIndex}`);
                if (!rack) {
                  return (
                    <div
                      key={`slot-${rowIndex}-${colIndex}`}
                      className={`${cellBase} border-dashed border-gray-500 bg-gray-700/40`}
                    >
                      <Plus className="w-4 h-4 text-gray-400" />
                    </div>
                  );
                }

                const rackType = rack.type ? rackTypes.find((r) => r.id === rack.type) : null;
                const serverCount = rack.servers ? rack.servers.length : 0;
                const utilization = rackType ? (serverCount / rackType.capacity * 100).toFixed(0) : 0;
                const status = getRackStatus(rack, rackType);
                const { border, glow, badge } = rackStatusClasses(status, rackType?.color ?? 'border-gray-500');

                const floorQualityClass =
                  rack.floorQuality === 'premium'
                    ? 'bg-gradient-to-br from-purple-600 to-indigo-600'
                    : rack.floorQuality === 'standard'
                    ? 'bg-gradient-to-br from-blue-600 to-sky-600'
                    : 'bg-gray-600';

                const hasFailed = (rack.servers ?? []).some(s => s.failureStatus === 'failed');

                return (
                  <div
                    key={`rack-${rack.id}-${rowIndex}-${colIndex}`}
                    onClick={() => setSelectedRack(rack.id)}
                    className={`${cellBase} relative cursor-pointer transition-all duration-150
                      ${selectedRack === rack.id ? 'ring-2 ring-yellow-400 scale-105 z-10' : 'hover:scale-105'}
                      ${rack.type ? (rackType?.color || 'bg-gray-500') : 'bg-gray-700 hover:bg-gray-600 border-dashed border-gray-500'}
                      ${rack.type ? floorQualityClass : ''}
                      ${rack.type ? border : ''}
                      ${glow}
                      ${hasFailed ? 'animate-pulse' : ''}
                    `}
                    title={
                      rack.type
                        ? `${rackType?.name} — ${serverCount}/48 (${utilization}%) — PDU: ${rackType?.power ?? 0}kW${status === 'critical' ? ' ⚠ SERVER FAILED' : status === 'alert' ? ' ⚠ Repairing' : status === 'warning' ? ' ⚠ Near PDU limit' : ''}`
                        : `Empty rack slot`
                    }
                  >
                    {rack.type ? (
                      <>
                        {badge}
                        <Building className="w-3 h-3 mb-0.5 shrink-0" />
                        <span className="text-[10px] leading-none">{rack.servers.length}/48</span>
                      </>
                    ) : (
                      <Plus className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="mb-4 p-2 bg-gradient-to-r from-blue-900 to-indigo-800 rounded-lg border border-blue-700">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
          <span>Professional Raised Floor System</span>
          <span className="text-blue-400">Active</span>
        </div>
      </div>

      {/* Hot Aisle section (top) */}
      <div className="text-center py-3 mb-2 bg-gradient-to-r from-red-900 to-rose-700 rounded text-red-200 text-sm font-semibold">
        🔥 Hot Aisle - Server Exhaust
      </div>

      {/* First row of racks */}
      <div className="grid grid-cols-16 gap-1 mb-2 p-2 bg-gray-700/30 rounded border border-gray-600/50">
        {racks.slice(0, 16).map((rack) => {
          const rackType = rack.type ? rackTypes.find((r) => r.id === rack.type) : null;
          const serverCount = rack.servers ? rack.servers.length : 0;
          const utilization = rackType ? (serverCount / rackType.capacity * 100).toFixed(0) : 0;
          const status = getRackStatus(rack, rackType);
          const { border, glow, badge } = rackStatusClasses(status, rackType?.color ?? 'border-gray-500');
          const floorQualityClass =
            rack.floorQuality === 'premium'
              ? 'bg-gradient-to-br from-purple-600 to-indigo-600 shadow-lg shadow-purple-500/30'
              : rack.floorQuality === 'standard'
              ? 'bg-gradient-to-br from-blue-600 to-sky-600 shadow-lg shadow-blue-500/30'
              : 'bg-gray-600';
          const hasFailed = (rack.servers ?? []).some(s => s.failureStatus === 'failed');
          return (
            <div
              key={`rack-${rack.id}-top`}
              onClick={() => setSelectedRack(rack.id)}
              className={`relative w-14 h-14 box-border rounded flex items-center justify-center cursor-pointer transition-all duration-150 border-2
                ${selectedRack === rack.id ? 'ring-4 ring-offset-2 ring-offset-gray-800 ring-yellow-400 scale-110 z-10' : 'hover:scale-105'}
                ${rack.type ? rackType?.color || 'bg-gray-500' : 'bg-gray-700 hover:bg-gray-600 border-dashed border-gray-500'}
                ${rack.type ? floorQualityClass : ''}
                ${rack.type ? border : ''}
                ${glow}
                ${hasFailed ? 'animate-pulse' : ''}
              `}
              title={rack.type
                ? `${rackType?.name} — ${serverCount}/48 (${utilization}%) — PDU: ${rackType?.power ?? 0}kW${status === 'critical' ? ' ⚠ SERVER FAILED' : status === 'alert' ? ' ⚠ Repairing' : status === 'warning' ? ' ⚠ Near PDU limit' : ''}`
                : `Empty Rack Slot ${rack.id}`}
            >
              {rack.type ? (
                <div className="text-center">
                  {badge}
                  <Building className="w-4 h-4 mx-auto mb-1" />
                  <div className="text-xs">{rack.servers.length}/48</div>
                </div>
              ) : (
                <Plus className="w-6 h-6 text-gray-400" />
              )}
            </div>
          );
        })}
      </div>

      {/* Cold Aisle section (middle) */}
      <div className="text-center py-3 mb-2 bg-gradient-to-r from-cyan-900 to-blue-700 rounded text-blue-200 text-sm font-semibold">
        ❄️ Cold Aisle - Cool Air Intake
      </div>

      {/* Second row of racks */}
      <div className="grid grid-cols-16 gap-1 mb-2 p-2 bg-gray-700/30 rounded border border-gray-600/50">
        {racks.slice(16, 32).map((rack) => {
          const rackType = rack.type ? rackTypes.find((r) => r.id === rack.type) : null;
          const serverCount = rack.servers ? rack.servers.length : 0;
          const utilization = rackType ? (serverCount / rackType.capacity * 100).toFixed(0) : 0;
          const status = getRackStatus(rack, rackType);
          const { border, glow, badge } = rackStatusClasses(status, rackType?.color ?? 'border-gray-500');
          const floorQualityClass =
            rack.floorQuality === 'premium'
              ? 'bg-gradient-to-br from-purple-600 to-indigo-600 shadow-lg shadow-purple-500/30'
              : rack.floorQuality === 'standard'
              ? 'bg-gradient-to-br from-blue-600 to-sky-600 shadow-lg shadow-blue-500/30'
              : 'bg-gray-600';
          const hasFailed = (rack.servers ?? []).some(s => s.failureStatus === 'failed');
          return (
            <div
              key={`rack-${rack.id}-bottom`}
              onClick={() => setSelectedRack(rack.id)}
              className={`relative w-14 h-14 box-border rounded flex items-center justify-center cursor-pointer transition-all duration-150 border-2
                ${selectedRack === rack.id ? 'ring-4 ring-offset-2 ring-offset-gray-800 ring-yellow-400 scale-110 z-10' : 'hover:scale-105'}
                ${rack.type ? rackType?.color || 'bg-gray-500' : 'bg-gray-700 hover:bg-gray-600 border-dashed border-gray-500'}
                ${rack.type ? floorQualityClass : ''}
                ${rack.type ? border : ''}
                ${glow}
                ${hasFailed ? 'animate-pulse' : ''}
              `}
              title={rack.type
                ? `${rackType?.name} — ${serverCount}/48 (${utilization}%) — PDU: ${rackType?.power ?? 0}kW${status === 'critical' ? ' ⚠ SERVER FAILED' : status === 'alert' ? ' ⚠ Repairing' : status === 'warning' ? ' ⚠ Near PDU limit' : ''}`
                : `Empty Rack Slot ${rack.id}`}
            >
              {rack.type ? (
                <div className="text-center">
                  {badge}
                  <Building className="w-4 h-4 mx-auto mb-1" />
                  <div className="text-xs">{rack.servers.length}/48</div>
                </div>
              ) : (
                <Plus className="w-6 h-6 text-gray-400" />
              )}
            </div>
          );
        })}
      </div>

      {/* Hot Aisle section (bottom) */}
      <div className="text-center py-3 mb-2 bg-gradient-to-r from-red-900 to-rose-700 rounded text-red-200 text-sm font-semibold">
        🔥 Hot Aisle - Server Exhaust
      </div>
    </div>
  );
};

export default React.memo(RackGrid);
